import { useEffect, useLayoutEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router';
import { ChatCallRoot } from '@saas/module-chat/ui';
import { ActionMenu, Avatar, Icon, IconButton, useAuth, useTheme } from '@saas/ui';

/**
 * チャット専用シェル。業務コンソールのヘッダー・業務メニュー・下部タブを持たず、
 * 会話一覧・本文・スレッドへブラウザー全体を割り当てる。
 */
export function ChatShell() {
  return (
    <ChatCallRoot>
      <ChatShellLayout />
    </ChatCallRoot>
  );
}

function ChatShellLayout() {
  const { me, logout } = useAuth();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const externalGuest = Boolean(me?.roles.includes('guest') && !me.roles.some((r) => r !== 'guest'));

  // チャット専用画面は PC で情報量を優先し、文字・余白・アイコンを一律 80% にする。
  // class は html に付けるため、document.body に portal されるメニューやプロフィールカードにも反映される。
  useLayoutEffect(() => {
    document.documentElement.classList.add('chat-compact');
    return () => document.documentElement.classList.remove('chat-compact');
  }, []);

  useEffect(() => {
    const previous = document.title;
    document.title = `チャット | ${me?.tenant.name ?? 'ooak'}`;
    return () => {
      document.title = previous;
    };
  }, [me?.tenant.name]);

  return (
    <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-base">
      <a href="#chat-main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-input focus:bg-card focus:px-4 focus:py-2">
        チャット本文へ移動
      </a>
      <header className="z-40 flex h-12 shrink-0 items-center gap-1 border-b border-line bg-card px-2 md:h-10 md:px-3">
        <Link to="/chat" className="flex min-w-0 items-center gap-1.5 rounded-input px-1.5 py-0.5 hover:bg-sunken">
          <span className="font-serif text-lg font-semibold leading-6">ooak</span>
          <span className="hidden text-caption text-fg-secondary sm:inline">chat</span>
        </Link>
        <span className="hidden h-4 w-px bg-line sm:block" aria-hidden="true" />
        <span className="hidden max-w-36 truncate text-caption font-medium sm:block">{me?.tenant.name}</span>
        <Link
          to="/chat/search"
          className="mx-auto hidden min-h-8 w-full max-w-md items-center gap-1.5 rounded-input border border-line bg-sunken px-2.5 text-caption text-fg-secondary hover:border-line-strong hover:text-fg md:flex"
        >
          <Icon name="search" size={20} />
          メッセージを検索
        </Link>
        <div className="flex-1 md:hidden" />
        {!externalGuest ? <a href="/" className="hidden min-tap items-center gap-1.5 rounded-input px-2 text-caption text-fg-secondary hover:bg-sunken hover:text-fg sm:flex md:!min-h-8">
          <Icon name="open_in_new" size={20} />
          業務コンソール
        </a> : null}
        <IconButton
          icon={resolved === 'dark' ? 'light_mode' : 'dark_mode'}
          label={resolved === 'dark' ? 'ライトモードに切り替え' : 'ダークモードに切り替え'}
          size={20}
          className="md:!min-h-8 md:!min-w-8"
          onClick={toggle}
        />
        <ActionMenu
          className="md:[&>button]:!min-h-8 md:[&>button]:!px-1.5"
          label={<Avatar name={me?.user.displayName} size={24} />}
          items={[
            ...(!externalGuest ? [{ label: '業務コンソール', icon: 'open_in_new' as const, onSelect: () => navigate('/') }] : []),
            { label: 'プロフィール・パスワード', icon: 'person', onSelect: () => navigate('/settings/profile') },
            { label: '通知設定', icon: 'notifications', onSelect: () => navigate('/settings/notifications') },
            { label: 'ログアウト', icon: 'logout', onSelect: () => void logout() },
          ]}
        />
      </header>
      <main id="chat-main" tabIndex={-1} className="flex min-h-0 flex-1 outline-none">
        <Outlet />
      </main>
    </div>
  );
}
